/**
 * Pure decision helpers for the publish dropdown.
 *
 * Kept out of PublishDropdown.tsx so the publish/no-op decision can be unit tested
 * without mounting the component (which pulls in Firebase, four stores and a dozen hooks).
 */

interface NothingToPublishArgs {
  /** Uncommitted working-tree changes, from GET /commits/check-changes */
  hasUncommittedChanges: boolean;
  /** commitSha of the deploy current.json points at, or null if unknown */
  publishedCommitSha: string | null;
  /** commitSha of the newest commit in the project, or null if unknown */
  latestCommitSha: string | null;
}

/**
 * Whether a publish would be a pure no-op.
 *
 * True only when we can *prove* the live site already serves the latest work:
 * the working tree is clean AND the published commit equals the latest commit.
 *
 * Returns false whenever either SHA is unknown — we must never suppress a publish
 * on missing data, because the fast path is the only way some users (e.g. anyone
 * editing directly in VS Code, where nothing auto-commits) get their work live.
 */
export const isNothingToPublish = ({
  hasUncommittedChanges,
  publishedCommitSha,
  latestCommitSha,
}: NothingToPublishArgs): boolean => {
  if (hasUncommittedChanges) return false;
  if (!publishedCommitSha || !latestCommitSha) return false;
  return publishedCommitSha === latestCommitSha;
};
